import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_PAGE_SIZE = 25;

/**
 * Keeps page/rowsPerPage in the URL (?page=&limit=) instead of local state,
 * so returning via browser back from a detail route restores the same page
 * instead of resetting to page 1.
 */
export function usePaginationParams(defaultLimit: number = DEFAULT_PAGE_SIZE) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => {
    const value = Number(searchParams.get('page'));
    return Number.isFinite(value) && value > 0 ? value : 1;
  }, [searchParams]);

  const rowsPerPage = useMemo(() => {
    const value = Number(searchParams.get('limit'));
    return Number.isFinite(value) && value > 0 ? value : defaultLimit;
  }, [searchParams, defaultLimit]);

  const setPage = useCallback(
    (nextPage: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', String(nextPage));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setRowsPerPage = useCallback(
    (nextLimit: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('limit', String(nextLimit));
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    searchParams,
    setSearchParams,
  } as const;
}
